/**
 * What can be bought, and what it is worth in credits.
 *
 * Credits live here rather than in Whop plan metadata because they are our
 * product, not Whop's: a plan's credit allowance is checked in tests, read in
 * review, and must not be silently changeable from a dashboard by anyone who
 * can log into Whop. Money is the mirror image: the app shows the amount the
 * live Whop plan carries, so a price edited on Whop is shown as it is charged.
 * The amounts below are what `scripts/whop-create-plans.mjs` creates the plans
 * at, and what a page shows when Whop cannot be reached.
 *
 * The join between the two is `lookupKey`: a Whop plan under the product named
 * by WHOP_PRODUCT_ID whose `metadata.krevya_lookup_key` is that key makes the
 * plan purchasable. No plan id ever has to live in an environment variable.
 */

/**
 * What one credit is worth, and what one creative costs in them.
 *
 * These two numbers are the whole pricing scheme and they are written down
 * once, here, because everything else is derived from them and because they
 * had silently disagreed: the plan blurbs counted at ten credits a creative
 * while the default model charged thirty, so Starter promised twenty
 * creatives and delivered six. A number in a sentence is a price, and it has
 * to come from the same place the charge does.
 *
 * `CREDIT_USD` is the conversion every `credit_per_image` and
 * `credit_per_second` in `model_profiles` was derived from: a model's rate is
 * its real provider cost, times the markup that line of work carries, divided
 * by this. It was the Growth plan read back ($69 / 550). The plans now carry
 * twice the credits at the same prices (Growth is $69 / 1,200, about $0.06 a
 * credit), a decision to give more images per dollar, so a plan credit now
 * sells for less than this and the model rates were deliberately left where
 * they were. Re-deriving them from the new figure is a pricing decision, not a
 * refactor.
 *
 * `STANDARD_CREATIVE_CREDITS` is the default image model's rate, so it is the
 * unit the plans are sold in and what "about N creatives" counts.
 */
export const CREDIT_USD = 0.125;
export const STANDARD_CREATIVE_CREDITS = 10;

/** How many standard creatives a plan's credits buy. What the blurb says. */
export function creativesPerMonth(credits: number): number {
  return Math.floor(credits / STANDARD_CREATIVE_CREDITS);
}

export type PlanInterval = 'month' | 'year';

/**
 * A yearly plan is paid once and grants its credits monthly: this many grants
 * per paid year, the first one when the invoice is paid.
 */
export const ANNUAL_CREDIT_MONTHS = 12;

/**
 * The currencies a plan is sold in.
 *
 * Each is its own set of Whop plans, priced by hand for its market rather
 * than converted at a rate: ₺999 reads as a price, ₺1,013.47 reads as a
 * conversion. Credits are the same in every currency, so what a merchant gets
 * does not depend on where they are, only what they pay for it.
 */
export const SUPPORTED_CURRENCIES = ['usd', 'try', 'eur'] as const;
export type Currency = (typeof SUPPORTED_CURRENCIES)[number];
export const DEFAULT_CURRENCY: Currency = 'usd';

export function isCurrency(value: unknown): value is Currency {
  return typeof value === 'string' && (SUPPORTED_CURRENCIES as readonly string[]).includes(value);
}

/** A currency named by a query string, a cookie or a request body; null for anything else. */
export function parseCurrency(value: unknown): Currency | null {
  if (typeof value !== 'string') return null;
  const lower = value.trim().toLowerCase();
  return isCurrency(lower) ? lower : null;
}

/**
 * The countries shown euros first: the European Union and the rest of the
 * European Economic Area. A few of them do not pay in euros at home, but a
 * euro price is closer to their card than a dollar one, and anyone can switch.
 */
const EURO_COUNTRIES: ReadonlySet<string> = new Set([
  // The European Union.
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
  'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  // The rest of the EEA.
  'IS', 'LI', 'NO',
]);

/**
 * The currency a visitor sees before they pick one, from the two-letter
 * country Cloudflare puts on the request (`cf-ipcountry`). Turkey sees lira,
 * Europe euros, and everyone else dollars, including every request that names
 * no country: a local run, Tor's `T1`, Cloudflare's unknown `XX`.
 */
export function currencyForCountry(country: string | null | undefined): Currency {
  const code = (country ?? '').trim().toUpperCase();
  if (code === 'TR') return 'try';
  if (EURO_COUNTRIES.has(code)) return 'eur';
  return DEFAULT_CURRENCY;
}

/** A plan's price in one currency, in that currency's minor unit. */
export type PlanPrice = {
  month: number;
  /** Null where the year is not sold in this currency (Partner outside dollars). */
  year: number | null;
};

export type BillingPlan = {
  id: string;
  name: string;
  /**
   * The `metadata.krevya_lookup_key` of the monthly dollar Whop plan. Every
   * other currency adds its code to it (`krevya_growth_monthly_try`); the
   * dollar key keeps its old spelling because live memberships already carry
   * it. `lookupKeyFor` is the one place the rule is written.
   */
  lookupKey: string;
  /**
   * The lookup key of the yearly dollar Whop plan, created by
   * `scripts/whop-create-plans.mjs`. Null for a plan sold by the month only.
   */
  yearlyLookupKey: string | null;
  /**
   * The list prices, per currency, in minor units. What the setup script
   * creates each Whop plan at, what a page shows when Whop cannot be reached,
   * and what a plan that is not self-serve is quoted at while Whop has no plan
   * for it. Whop's own amount wins wherever it has one.
   */
  prices: Readonly<Record<Currency, PlanPrice>>;
  /** `prices.usd.month`, kept for readers written before there were currencies. */
  monthlyAmountMinor: number;
  /** `prices.usd.year`, kept for readers written before there were currencies. */
  yearlyAmountMinor: number | null;
  /** Credits added to the workspace for every month this plan is paid for. */
  credits: number;
  highlight?: boolean;
  /**
   * How many boards Krevya may write by itself for this workspace in a day.
   *
   * Concepts are cheap but not free, a board is roughly two cents of tokens
   * and a workspace with forty products would otherwise have forty boards
   * written for it overnight. The cap is per plan because that is who is
   * paying for the tokens.
   */
  autoBoardsPerDay: number;
  /**
   * How many Shopify stores a workspace on this plan may have connected at
   * once; null is no limit. Checked when a new store is connected, never by
   * taking away a store that is already there.
   */
  storeLimit: number | null;
  /**
   * Whether the plan can be bought from the billing page. A plan that is not
   * is arranged by agreement: checkout refuses it, and the cards offer a
   * conversation instead of a button. Once its Whop plan exists and a
   * membership is set up by hand, payments, credits and limits follow it
   * through the same paths as any other plan, because they go by lookup key.
   */
  selfServe: boolean;
};

export const BILLING_PLANS: readonly BillingPlan[] = [
  {
    id: 'starter',
    name: 'Starter',
    lookupKey: 'krevya_starter_monthly',
    yearlyLookupKey: 'krevya_starter_yearly',
    monthlyAmountMinor: 2900,
    yearlyAmountMinor: 27900,
    prices: {
      usd: { month: 2900, year: 27900 },
      try: { month: 99900, year: 959000 },
      eur: { month: 2700, year: 25900 },
    },
    credits: 400,
    autoBoardsPerDay: 2,
    storeLimit: 1,
    selfServe: true,
  },
  {
    id: 'growth',
    name: 'Growth',
    lookupKey: 'krevya_growth_monthly',
    yearlyLookupKey: 'krevya_growth_yearly',
    monthlyAmountMinor: 6900,
    yearlyAmountMinor: 65900,
    prices: {
      usd: { month: 6900, year: 65900 },
      try: { month: 239900, year: 2299000 },
      eur: { month: 6500, year: 61900 },
    },
    credits: 1200,
    highlight: true,
    autoBoardsPerDay: 5,
    storeLimit: 5,
    selfServe: true,
  },
  {
    id: 'pro',
    name: 'Pro',
    lookupKey: 'krevya_pro_monthly',
    yearlyLookupKey: 'krevya_pro_yearly',
    monthlyAmountMinor: 14900,
    yearlyAmountMinor: 142900,
    prices: {
      usd: { month: 14900, year: 142900 },
      try: { month: 499900, year: 4799000 },
      eur: { month: 13900, year: 132900 },
    },
    credits: 3000,
    autoBoardsPerDay: 12,
    storeLimit: 20,
    selfServe: true,
  },
  {
    // Agencies and high-volume brands, arranged by agreement on either
    // interval: its Whop plan is hidden and the membership set up by hand,
    // after which it is billed and granted like the others. The year is
    // the same 20% under twelve months as the self-serve plans ($4,790, about
    // $399.17 a month), so the yearly view can quote it rather than leave
    // Partner as the one card without a number. Outside dollars the year has
    // no list price yet and is a conversation.
    id: 'partner',
    name: 'Partner',
    lookupKey: 'krevya_partner_monthly',
    yearlyLookupKey: 'krevya_partner_yearly',
    monthlyAmountMinor: 49900,
    yearlyAmountMinor: 479000,
    prices: {
      usd: { month: 49900, year: 479000 },
      try: { month: 1699900, year: null },
      eur: { month: 46900, year: null },
    },
    credits: 10000,
    autoBoardsPerDay: 30,
    storeLimit: null,
    selfServe: false,
  },
] as const;

/**
 * The one-time Krevya Theme licence, for a merchant who wants the theme on its
 * own without a Krevya subscription (the way Shrine and other paid themes sell
 * a lifetime licence). It is not a plan: no credits, no renewal, no limits. It
 * grants one thing — the theme may run on the shop, for good.
 *
 * A Krevya subscriber already has this through their subscription, so the
 * purchase page strikes the price through and reads "free" for them. Prices
 * are in each currency's minor unit (cents, kuruş): $99, ₺4.499, €99. The Whop
 * one-time plan carries `krevya_theme_license` for dollars, the currency added
 * for the rest, the same rule `lookupKeyFor` writes for subscriptions.
 */
export const THEME_LICENSE = {
  lookupKey: 'krevya_theme_license',
  prices: {
    usd: 9900,
    try: 449900,
    eur: 9900,
  },
} as const satisfies { lookupKey: string; prices: Readonly<Record<Currency, number>> };

/** The one-time theme licence's Whop lookup key for a currency (dollars keep the base). */
export function themeLicenseLookupKey(currency: Currency): string {
  return currency === 'usd' ? THEME_LICENSE.lookupKey : `${THEME_LICENSE.lookupKey}_${currency}`;
}

/** Whether a lookup key is the one-time theme licence, in any currency. */
export function isThemeLicenseLookupKey(key: string | null | undefined): boolean {
  if (!key) return false;
  return SUPPORTED_CURRENCIES.some((currency) => themeLicenseLookupKey(currency) === key);
}

/** The currency a theme-licence lookup key names; dollars when the base key or unknown. */
export function themeLicenseCurrency(key: string | null | undefined): Currency {
  const found = SUPPORTED_CURRENCIES.find((currency) => themeLicenseLookupKey(currency) === key);
  return found ?? 'usd';
}

/**
 * The allowance for a workspace that is not paying for anything.
 *
 * One a day: enough that somebody who signed up and left comes back to
 * something they did not have, small enough that a dormant free account
 * costs cents a month rather than dollars.
 */
export const AUTO_BOARDS_WITHOUT_PLAN = 1;

/** How many boards may be written unasked for a workspace on this plan. */
export function autoBoardsPerDay(planId: string | null | undefined): number {
  if (!planId) return AUTO_BOARDS_WITHOUT_PLAN;
  return findPlan(planId)?.autoBoardsPerDay ?? AUTO_BOARDS_WITHOUT_PLAN;
}

/**
 * How many stores a workspace with no active plan may connect. One, so the
 * product can be tried end to end before anything is bought.
 */
export const STORE_LIMIT_WITHOUT_PLAN = 1;

/**
 * The store limit of a workspace, from the plans it is actively paying for.
 *
 * Usually one plan; two while a monthly plan runs out beside the yearly one
 * that replaced it, and then the more generous of the two holds, since both
 * are paid for. Null is no limit. A plan id this catalogue does not know
 * counts as no plan.
 */
export function storeLimitFor(planIds: readonly (string | null | undefined)[]): number | null {
  const plans = planIds
    .map((id) => (id ? findPlan(id) : undefined))
    .filter((plan): plan is BillingPlan => plan !== undefined);
  if (plans.length === 0) return STORE_LIMIT_WITHOUT_PLAN;
  if (plans.some((plan) => plan.storeLimit === null)) return null;
  return Math.max(...plans.map((plan) => plan.storeLimit ?? 0));
}

export function findPlan(planId: string): BillingPlan | undefined {
  return BILLING_PLANS.find((plan) => plan.id === planId);
}

/**
 * The lookup key a plan is sold at for an interval and currency; null when it
 * is not sold that way.
 *
 * Dollars keep the keys they had before there were currencies, because every
 * membership already paid for carries one of them; another currency adds its
 * code (`krevya_growth_yearly_eur`).
 */
export function lookupKeyFor(
  plan: BillingPlan,
  interval: PlanInterval,
  currency: Currency = DEFAULT_CURRENCY,
): string | null {
  const base = interval === 'year' ? plan.yearlyLookupKey : plan.lookupKey;
  if (!base) return null;
  if (interval === 'year' && plan.prices[currency].year === null) return null;
  return currency === DEFAULT_CURRENCY ? base : `${base}_${currency}`;
}

type KeyEntry = { plan: BillingPlan; interval: PlanInterval; currency: Currency };

/** Every lookup key the catalogue knows, and what it sells. Built once. */
const KEY_INDEX: ReadonlyMap<string, KeyEntry> = new Map(
  BILLING_PLANS.flatMap((plan) =>
    SUPPORTED_CURRENCIES.flatMap((currency) =>
      (['month', 'year'] as const).flatMap((interval) => {
        const key = lookupKeyFor(plan, interval, currency);
        return key ? [[key, { plan, interval, currency }] as const] : [];
      }),
    ),
  ),
);

/** Resolves a lookup key of any interval and currency to its plan. */
export function planForLookupKey(lookupKey: string | null | undefined): BillingPlan | undefined {
  return lookupKey ? KEY_INDEX.get(lookupKey)?.plan : undefined;
}

/** Which billing interval a known lookup key belongs to; undefined for a key we do not sell. */
export function intervalForLookupKey(
  lookupKey: string | null | undefined,
): PlanInterval | undefined {
  return lookupKey ? KEY_INDEX.get(lookupKey)?.interval : undefined;
}

/** Which currency a known lookup key is sold in; undefined for a key we do not sell. */
export function currencyForLookupKey(lookupKey: string | null | undefined): Currency | undefined {
  return lookupKey ? KEY_INDEX.get(lookupKey)?.currency : undefined;
}

/** Every lookup key the catalogue sells, monthly and yearly, in one currency or all of them. */
export function catalogLookupKeys(currency?: Currency): string[] {
  return [...KEY_INDEX.entries()]
    .filter(([, entry]) => currency === undefined || entry.currency === currency)
    .map(([key]) => key);
}

/**
 * How much cheaper the year is than twelve months, as a whole percent. Computed
 * from the real Whop amounts rather than assumed, so a price someone edited
 * in the dashboard is shown honestly.
 */
export function savingsPercent(monthlyMinor: number, yearlyMinor: number): number {
  if (monthlyMinor <= 0) return 0;
  return Math.round((1 - yearlyMinor / (ANNUAL_CREDIT_MONTHS * monthlyMinor)) * 100);
}

/**
 * Which subscription states mean "this workspace is a paying customer".
 *
 * `past_due` counts: the last payment failed but Whop is still retrying and
 * the customer has not lost access. `incomplete` does not, that membership
 * has never been paid for.
 */
export const ACTIVE_SUBSCRIPTION_STATUSES = ['active', 'trialing', 'past_due'] as const;

export function isActiveSubscription(status: string): boolean {
  return (ACTIVE_SUBSCRIPTION_STATUSES as readonly string[]).includes(status);
}

/**
 * Money is stored and passed around in the currency's minor unit (cents,
 * kuruş). Formatted with `Intl` in the reader's locale, so a Turkish reader
 * sees ₺2.399,00 and an English one ₺2,399.00, and with the narrow symbol, so
 * an English page says ₺ rather than "TRY". `digits` fixes the decimals; by
 * default every amount carries two, the way a receipt does.
 */
export function formatMoney(
  minorUnits: number,
  currency: string,
  locale = 'en-US',
  digits = 2,
): string {
  const upper = currency.toUpperCase();
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: upper,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(minorUnits / 100);
  } catch {
    // A code Intl does not know is still money; it is written out plainly.
    return `${(minorUnits / 100).toFixed(digits)} ${upper}`;
  }
}
